import { useEffect, useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import { presetRange, type DateRangePresetId, type DateRangeValue } from '@/lib/date-range'
import { CurrencyToggle } from '@/components/volume/CurrencyToggle'
import { GranularityToggle } from '@/components/volume/GranularityToggle'
import { DrawFilter } from '@/components/volume/DrawFilter'
import { ChainScopeCaption } from '@/components/data/ChainScopeCaption'
import { EntryVolumeKpiCards } from '@/components/volume/EntryVolumeKpiCards'
import { EntryVolumeChart } from '@/components/volume/EntryVolumeChart'
import { useEntryVolume } from '@/hooks/api/useEntryVolume'
import { ACTIVE_CHAINS } from '@/config/chains'
import { ApiError } from '@/lib/api'
import type { EntryVolumeCurrency, EntryVolumeGranularity, EntryVolumeTotals } from '@/types/api'
import { searchEnum, searchString } from '@/lib/search-params'

export interface VolumeSearch {
    from?: string
    to?: string
    preset?: DateRangePresetId
    granularity?: EntryVolumeGranularity
    draw?: string
    currency?: EntryVolumeCurrency
}

const PRESET_IDS: ReadonlyArray<DateRangePresetId> = ['1h', '6h', '12h', '24h', '7d', '30d', '90d']
const GRANULARITIES: ReadonlyArray<EntryVolumeGranularity> = ['5m', 'hour', 'day', 'week']
// Service rejects `5m` for windows > 24h. Mirrored in GranularityToggle.
const FIVE_M_MAX_RANGE_MS = 24 * 60 * 60 * 1000
const CURRENCIES: ReadonlyArray<EntryVolumeCurrency> = ['eth', 'eur']

// Granularity rule: 5m for sub-day windows so the new 1h / 6h / 12h presets
// land on a useful number of buckets; hour for 24h-7d; week for >90d; day
// otherwise. The < 24h boundary aligns with the service-side 5m cap and the
// GranularityToggle's disable rule, so 24h-exactly defaults to hour (cleaner
// 24 buckets) while still letting the user opt into 5m.
function autoGranularity(from: Date, to: Date): EntryVolumeGranularity {
    const ms = to.getTime() - from.getTime()
    if (ms < 86_400_000) return '5m'
    const days = ms / 86_400_000
    if (days < 7) return 'hour'
    if (days > 90) return 'week'
    return 'day'
}

export const Route = createFileRoute('/volume')({
    component: VolumePage,
    validateSearch: (search): VolumeSearch => ({
        from: searchString(search.from),
        to: searchString(search.to),
        preset: searchEnum(search.preset, PRESET_IDS),
        granularity: searchEnum(search.granularity, GRANULARITIES),
        draw: searchString(search.draw),
        currency: searchEnum(search.currency, CURRENCIES),
    }),
})

function resolveRange(search: VolumeSearch, now: Date = new Date()): DateRangeValue {
    if (search.preset) return presetRange(search.preset, now)
    if (search.from && search.to) {
        const from = new Date(search.from)
        const to = new Date(search.to)
        if (!Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime())) {
            return { from, to }
        }
    }
    return presetRange('7d', now)
}

// Inner page renders the filters + KPI cards + chart given resolved search
// state and a setSearch callback. The outer Route does the URL plumbing.
// Pulled out as a discrete component so route tests can mount it without
// having to spin up the full app routeTree.
export interface VolumePageInnerProps {
    search: VolumeSearch
    setSearch: (next: VolumeSearch) => void
}

export function VolumePageInner({ search, setSearch }: VolumePageInnerProps) {
    // Memoize range on the SEARCH inputs, not on the resolved Date instances.
    // Without this `resolveRange` calls `new Date()` every render, range gets a
    // fresh identity, the queryParams useMemo invalidates, the queryKey
    // changes, and React Query refetches on every render.
    const { preset, from: searchFrom, to: searchTo } = search

    // Preset ranges are sliding windows ("Last 1h" should always mean the last
    // hour from *now*, not from when the page loaded). Tick `now` every 30s
    // when no explicit from/to is in the URL so the queryKey changes and the
    // chart's x-axis slides forward in lockstep with the poll cadence.
    // Explicit from/to skip the tick — those ranges should stay anchored.
    const hasExplicitRange = !preset && Boolean(searchFrom && searchTo)
    const [now, setNow] = useState(() => Date.now())
    useEffect(() => {
        if (hasExplicitRange) return
        const id = setInterval(() => setNow(Date.now()), 30_000)
        return () => clearInterval(id)
    }, [hasExplicitRange])

    const range = useMemo(
        () => resolveRange({ preset, from: searchFrom, to: searchTo }, new Date(now)),
        [preset, searchFrom, searchTo, now],
    )
    // If the user previously picked `5m` and then widened the range past the
    // 24h cap, fall back to the auto rule rather than firing a guaranteed-400
    // request.
    const rangeMs = range.to.getTime() - range.from.getTime()
    const requested = search.granularity ?? autoGranularity(range.from, range.to)
    const granularity =
        requested === '5m' && rangeMs > FIVE_M_MAX_RANGE_MS
            ? autoGranularity(range.from, range.to)
            : requested
    const currency: EntryVolumeCurrency = search.currency ?? 'eth'
    const draw = search.draw ?? 'all'

    // Single-chain MVP: pin to the first active chain. Multi-chain selector is
    // a follow-up if the operator stack ever runs more than one operator at once.
    const chainId = ACTIVE_CHAINS[0]?.id ?? 0

    const queryParams = useMemo(
        () => ({
            from: range.from.toISOString(),
            to: range.to.toISOString(),
            granularity,
            draw,
            currency,
        }),
        [range, granularity, draw, currency],
    )

    const query = useEntryVolume(queryParams)

    const handleRangeChange = (next: DateRangeValue) => {
        setSearch({
            ...search,
            preset: next.presetId,
            from: next.presetId ? undefined : next.from.toISOString(),
            to: next.presetId ? undefined : next.to.toISOString(),
            granularity: undefined,
        })
    }

    const handleCurrencyChange = (next: EntryVolumeCurrency) => {
        setSearch({ ...search, currency: next })
    }

    const handleDrawChange = (next: string) => {
        setSearch({ ...search, draw: next })
    }

    const handleGranularityChange = (next: EntryVolumeGranularity) => {
        setSearch({ ...search, granularity: next })
    }

    const totals: EntryVolumeTotals | undefined = query.data?.totals

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Volume</h1>
                <p className="text-muted-foreground text-sm">
                    Entry volume, fee components, and prizes paid across the selected window. ETH
                    defaults; toggle to EUR for the FX-snapped fiat view.
                </p>
                {query.data ? (
                    <ChainScopeCaption chainId={query.data.chainId} className="mt-1" />
                ) : null}
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <DateRangePicker value={range} onChange={handleRangeChange} />
                <GranularityToggle
                    value={granularity}
                    onChange={handleGranularityChange}
                    rangeMs={rangeMs}
                />
                <CurrencyToggle value={currency} onChange={handleCurrencyChange} />
                <DrawFilter chainId={chainId} value={draw} onChange={handleDrawChange} />
            </div>

            <EntryVolumeKpiCards totals={totals} currency={currency} isLoading={query.isLoading} />

            <Card>
                <CardHeader>
                    <CardTitle>Entries over time</CardTitle>
                    <CardDescription>
                        Stacked PEA / CF / OpFee components with a prize line overlay.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {query.isError && (
                        <div className="text-destructive text-sm">
                            {query.error instanceof ApiError && query.error.status === 401
                                ? 'Session expired — reload to re-authenticate.'
                                : `Could not load entry volume: ${query.error instanceof Error ? query.error.message : 'unknown error'}`}
                        </div>
                    )}
                    {!query.isError && (
                        <EntryVolumeChart
                            buckets={query.data?.buckets ?? []}
                            currency={currency}
                            granularity={granularity}
                        />
                    )}
                </CardContent>
            </Card>
        </div>
    )
}

function VolumePage() {
    const search = Route.useSearch()
    const navigate = useNavigate({ from: Route.fullPath })
    return (
        <VolumePageInner
            search={search}
            setSearch={(next) => navigate({ search: () => next, replace: true })}
        />
    )
}
